"use client"

import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Pencil } from 'lucide-react'
import { getClients, type ClientOrg } from '@/services/clients'
import { useClientSelection } from '@/contexts/client-selection'
import { useRouter } from 'next/navigation'
import { OnboardClientDialog } from './OnboardClientDialog'

export default function ClientSelectorDialog() {
  const router = useRouter()
  const { isOpen, closeSelector, setSelectedClient } = useClientSelection()
  // Enable the query only when the dialog is open and ensure fresh data on open
  const { data, isLoading } = useQuery({
    queryKey: ['clients', { page_size: 1000 }],
    queryFn: () => getClients({ page_size: 1000 }),
    enabled: isOpen,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnReconnect: true,
    refetchOnWindowFocus: false,
  })
  const [query, setQuery] = React.useState('')
  const [editOpen, setEditOpen] = React.useState(false)
  const [editClient, setEditClient] = React.useState<ClientOrg | null>(null)

  const items: ClientOrg[] = React.useMemo(() => {
    const list = (data?.items ?? []) as ClientOrg[]
    const uniqByName = new Map<string, ClientOrg>()
    for (const c of list) {
      if (!uniqByName.has(c.name)) uniqByName.set(c.name, c)
    }
    const result = Array.from(uniqByName.values())
    const q = query.trim().toLowerCase()
    return q ? result.filter((c) => c.name.toLowerCase().includes(q) || (c.domain || '').toLowerCase().includes(q)) : result
  }, [data, query])

  function selectClient(c: ClientOrg) {
    setSelectedClient({ id: c.id, name: c.name, public_id: (c as any).public_id })
    closeSelector()
    router.push('/')
  }

  function openEdit(c: ClientOrg): void {
    setEditClient(c)
    setEditOpen(true)
    closeSelector()
  }

  return (
    <>
    <Dialog open={isOpen} onOpenChange={(o) => (!o ? closeSelector() : null)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Select a client</DialogTitle>
          <DialogDescription>Choose a client to focus the dashboard and project lists.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Input placeholder="Search clients by name or domain" value={query} onChange={(e) => setQuery(e.target.value)} />
          <div className="max-h-[50vh] overflow-auto divide-y rounded-md border bg-white border-gray-200 divide-gray-200 dark:bg-neutral-900 dark:border-neutral-800 dark:divide-neutral-800">
            {isLoading && (
              <div className="p-4 text-sm text-gray-600 dark:text-gray-300">Loading…</div>
            )}
            {!isLoading && items.length === 0 && (
              <div className="p-4 text-sm text-gray-600 dark:text-gray-300">No clients found.</div>
            )}
            {items.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-2 p-3 hover:bg-gray-50 dark:hover:bg-neutral-800">
                <button onClick={() => selectClient(c)} className="min-w-0 flex-1 text-left">
                  <div className="font-medium truncate">{c.name}</div>
                  {c.domain && <div className="text-xs text-gray-600 dark:text-gray-400 truncate">{c.domain}</div>}
                </button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    openEdit(c)
                  }}
                  aria-label="Edit client"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => closeSelector()}>Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    <OnboardClientDialog open={editOpen} onOpenChangeAction={setEditOpen} client={editClient} />
    </>
  )
}
